// @ts-check
import { toAnesthesiaUnits } from '../utils/anesthesia-units'
import { createSelector, shallowEqualMemoize } from '../utils/selector-utils'
import {
  selectHasRecoveryPhase,
  selectSedativesBeingUsed,
} from './sedation-level'

const empty = {}
const rawEvents = caseData => caseData.events || empty
const getEventSelector = fn => createSelector(rawEvents, fn)

// note: we do "floor" here because that's how we think of
// time. Digital clocks show "14:30" even if actual time is 14:30:59.
// So by flooring both before calculating duration we will always
// get a total number of minutes that looks correct.
// otherwise if you start at 3:30:00 and end at 4:00:54.
// the report would say 3:30 - 4:00 but duration would be 31 minutes.
const getDurationMinutes = (start, end) =>
  start && end ? Math.floor(end / 60000) - Math.floor(start / 60000) : null
const createDurationMinutesSelector = (startEvent, endEvent) =>
  getEventSelector(events =>
    getDurationMinutes(events[startEvent], events[endEvent])
  )

const getEventPropSelector = propName =>
  createSelector(rawEvents, raw => raw[propName])

const selectCaseStartTime = getEventPropSelector('caseStart')
const selectSurgeryStartTime = getEventPropSelector('surgeryStart')
const selectSurgeryEndTime = getEventPropSelector('surgeryEnd')
const selectRecoveryStartTime = getEventPropSelector('recoveryStart')
const selectVitalsEndTime = getEventPropSelector('vitalsEnd')

const selectCaseEndTime = createSelector(
  selectSurgeryStartTime,
  selectSurgeryEndTime,
  getEventPropSelector('end'),
  selectVitalsEndTime,
  selectSedativesBeingUsed,
  (surgeryStart, surgeryEnd, caseEnd, vitalsEnd, sedativesBeingUsed) => {
    if (sedativesBeingUsed) {
      return caseEnd
    } else {
      if (!vitalsEnd) return
      if (surgeryStart && surgeryEnd) {
        return Math.max(surgeryEnd, vitalsEnd)
      }
      if (!surgeryStart && !surgeryEnd) {
        return vitalsEnd
      }
    }
  }
)

const selectCaseInRecovery = createSelector(
  selectHasRecoveryPhase,
  selectRecoveryStartTime,
  selectCaseEndTime,
  (hasRecoveryPhase, recoveryStart, caseEnd) =>
    !!(hasRecoveryPhase && recoveryStart && !caseEnd)
)

const selectCaseComplete = createSelector(selectCaseEndTime, Boolean)

const selectCaseInProgress = createSelector(
  selectCaseStartTime,
  selectCaseEndTime,
  selectRecoveryStartTime,
  selectSedativesBeingUsed,
  (caseStartTime, caseEndTime, recoveryStartTime, sedativesBeingUsed) => {
    if (!caseStartTime) return false
    return sedativesBeingUsed ? !recoveryStartTime : !caseEndTime
  }
)
const selectCaseWasStarted = getEventSelector(raw => !!raw.caseStart)
const selectIvStarted = getEventSelector(raw => !!raw.ivStart)
const selectRecoveryWasStarted = getEventSelector(raw => !!raw.recoveryStart)
const selectCaseStatus = createSelector(
  selectCaseComplete,
  selectCaseInRecovery,
  selectCaseInProgress,
  selectIvStarted,
  selectCaseWasStarted,
  (complete, inRecovery, inProgress, ivStarted, caseWasStarted) => {
    if (complete) return 'complete'
    if (inRecovery) return 'recovery'
    if (inProgress) return 'inProgress'
    if (ivStarted) return 'ivStarted'
    if (!caseWasStarted) return 'notStarted'
  }
)

const selectAnesthesiaMinutes = createDurationMinutesSelector(
  'anesthesiaStart',
  'anesthesiaEnd'
)

export const eventSelectors = {
  selectEventTimesRaw: rawEvents,
  selectCaseStartTime,
  selectAnesthesiaStartTime: getEventPropSelector('anesthesiaStart'),
  selectAnesthesiaEndTime: getEventPropSelector('anesthesiaEnd'),
  selectSurgeryStartTime,
  selectSurgeryEndTime,
  selectRecoveryStartTime: getEventPropSelector('recoveryStart'),
  selectVitalsEndTime,
  selectCaseEndTime,
  selectIvStarted,
  selectCaseWasStarted,
  selectRecoveryWasStarted,
  selectCaseComplete,
  selectCaseInProgress: selectCaseInProgress,
  selectCaseInRecovery,
  selectCaseStatus,
  selectAnesthesiaMinutes,
  selectAnesthesiaUnits: createSelector(
    selectAnesthesiaMinutes,
    toAnesthesiaUnits
  ),
  selectSurgeryMinutes: createDurationMinutesSelector(
    'surgeryStart',
    'surgeryEnd'
  ),
  selectMonitoringMinutes: createDurationMinutesSelector(
    'caseStart',
    'vitalsEnd'
  ),
  selectRecoveryMinutes: createDurationMinutesSelector('recoveryStart', 'end'),
  selectCompleteCaseMinutes: createSelector(
    selectCaseStartTime,
    selectCaseEndTime,
    getDurationMinutes
  ),
}

export const optionSelectCaseStartOrAppTimeDay = shallowEqualMemoize(
  /**
   * @param {CaseData} caseData
   * @param {number} appTimeDay
   * @returns {number}
   */ (caseData, appTimeDay) => {
    const caseStartTime = eventSelectors.selectCaseStartTime(caseData)
    return caseStartTime || appTimeDay
  }
)

/**
 * @param {{
 *   [id in CaseEventType]?: number | null
 * }} updates
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdateEventTimes = updates => {
  const toReturn = {}
  for (const key in updates) {
    toReturn[`events.${key}`] = updates[key]
  }
  return toReturn
}
